/**
 * Follow-up questions when advise is provisional / low-confidence.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export default function CitizenFollowUpPanel({
  questions = [],
  reason = null,
  onSubmit = null,
  onSkip = null,
  busy = false,
}) {
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    setAnswers({});
  }, [questions.map((q) => q.id).join('|')]);

  if (!Array.isArray(questions) || !questions.length) return null;

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const requiredMissing = questions
    .filter((q) => q.required)
    .filter((q) => answers[q.id] === undefined || answers[q.id] === null || answers[q.id] === '');

  const reasonLabel = {
    no_rule_match: 'We need a bit more detail to pick the right path.',
    low_confidence: 'This path is provisional — a couple answers will sharpen it.',
    incomplete_scope: 'A few details will complete requirements for this use.',
    missing_sqft: 'Size helps estimate fees and review level.',
    adu_program: 'SF ADU State vs Local programs have different steps.',
    shed_size: 'Shed size decides whether a building permit is likely required.',
    clarify: 'A few follow-ups will refine your application path.',
  }[reason] || 'Answer a few questions to refine your path.';

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'warning.light',
        bgcolor: 'rgba(255, 243, 224, 0.55)',
      }}
      data-testid="citizen-follow-up-panel"
    >
      <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }} icon={false}>
        <Typography variant="subtitle2" fontWeight={700}>
          Quick follow-ups
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {reasonLabel}
        </Typography>
      </Alert>

      <Stack spacing={1.5}>
        {questions.map((q) => (
          <Box key={q.id}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {q.label}
              {q.required ? ' *' : ''}
            </Typography>
            {q.type === 'boolean' && (
              <FormControl>
                <RadioGroup
                  row
                  value={answers[q.id] === true ? 'yes' : answers[q.id] === false ? 'no' : ''}
                  onChange={(e) => setAnswer(q.id, e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            )}
            {q.type === 'select' && (
              <TextField
                select
                size="small"
                fullWidth
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                data-testid={`follow-up-${q.id}`}
              >
                {(q.options || []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            )}
            {q.type === 'number' && (
              <TextField
                type="number"
                size="small"
                fullWidth
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswer(q.id, e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0 }}
              />
            )}
            {q.type !== 'boolean' && q.type !== 'select' && q.type !== 'number' && (
              <TextField
                size="small"
                fullWidth
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
              />
            )}
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          disabled={busy || requiredMissing.length > 0 || Object.keys(answers).length === 0}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={() => onSubmit?.(answers)}
          sx={{
            textTransform: 'none',
            fontWeight: 650,
            bgcolor: 'var(--citizen-primary, #1565c0)',
            '&:hover': { bgcolor: 'var(--citizen-primary, #1565c0)', filter: 'brightness(0.92)' },
          }}
          data-testid="citizen-follow-up-submit"
        >
          Update my path
        </Button>
        {typeof onSkip === 'function' && (
          <Button
            variant="text"
            size="small"
            disabled={busy}
            onClick={() => onSkip()}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
            data-testid="citizen-follow-up-skip"
          >
            Continue with best guess
          </Button>
        )}
      </Stack>
    </Box>
  );
}
